import { createEffect, createEvent, createStore, sample } from 'effector';
import { httpUnauthorized } from '@/api';
import { loggedOut } from './auth';
import { navigated, reportSelected, type View } from './router';
import {
  $progressPct,
  $searchStatus,
  cancelSearchFx,
  fetchCurrentFx,
  searchSnapshotReceived,
  searchStarted,
  type SearchStatus,
} from './search';

export type TaskState = 'pending' | 'now' | 'done';
export type TaskRuntime = { id: number; state: TaskState; progress: number };

const TASK_COUNT = 5;

const initialTasks = (): TaskRuntime[] =>
  Array.from({ length: TASK_COUNT }, (_, i) => ({ id: i + 1, state: 'pending', progress: 0 }));

export const processingReset = createEvent();
export const taskAdvanced = createEvent<{ id: number; state: TaskState; progress: number }>();
export const processingFinished = createEvent<SearchStatus>();

const TERMINAL_STATUSES: ReadonlySet<SearchStatus> = new Set(['completed', 'failed', 'cancelled']);

export const $tasks = createStore<TaskRuntime[]>(initialTasks())
  .on(processingReset, () => initialTasks())
  .on(taskAdvanced, (s, { id, state, progress }) =>
    s.map((t) => (t.id === id ? { ...t, state, progress } : t)),
  );

const STATUS_TO_STEP: Record<SearchStatus, number> = {
  queued: 1,
  running: 2,
  filtering: 3,
  building: 4,
  completed: 5,
  failed: 5,
  cancelled: 5,
};

let pollTimer: ReturnType<typeof setInterval> | null = null;
const POLL_INTERVAL_MS = 5000;

export const startPollingFx = createEffect(() => {
  if (pollTimer) return;
  void fetchCurrentFx();
  pollTimer = setInterval(() => {
    void fetchCurrentFx();
  }, POLL_INTERVAL_MS);
});

export const stopPollingFx = createEffect(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
});

const applyProgressFx = createEffect<{ status: SearchStatus | null; progressPct: number }, void>(
  ({ status, progressPct }) => {
    if (!status) return;
    const currentStep = STATUS_TO_STEP[status];
    for (let i = 1; i <= TASK_COUNT; i += 1) {
      if (i < currentStep) {
        taskAdvanced({ id: i, state: 'done', progress: 100 });
      } else if (i === currentStep) {
        taskAdvanced({ id: i, state: 'now', progress: progressPct });
      } else {
        taskAdvanced({ id: i, state: 'pending', progress: 0 });
      }
    }
    if (TERMINAL_STATUSES.has(status)) processingFinished(status);
  },
);

sample({
  clock: navigated,
  filter: (v) => v === 'processing',
  target: [processingReset, startPollingFx],
});

sample({
  clock: navigated,
  filter: (v) => v !== 'processing',
  target: stopPollingFx,
});

// Применяем прогресс на КАЖДЫЙ полученный snapshot (а не на изменение
// store-store) — иначе при повторных snapshot'ах с тем же status sample
// не триггерится, и UI висит с initial 0% / pending.
sample({
  clock: searchSnapshotReceived,
  fn: (snap) => ({ status: snap.status, progressPct: snap.progressPct }),
  target: applyProgressFx,
});

// Когда заходим на /processing — сразу применить уже известное состояние
// из стора (если успели его получить через resumeFx до перехода). Это
// убирает «мигание» 0% между processingReset и первым новым опросом.
sample({
  clock: navigated,
  source: { status: $searchStatus, progressPct: $progressPct },
  filter: ({ status }, view) => view === 'processing' && status !== null,
  fn: ({ status, progressPct }) => ({ status, progressPct }),
  target: applyProgressFx,
});

sample({
  clock: processingFinished,
  fn: (status): View => (status === 'completed' ? 'report' : 'onboard'),
  target: navigated,
});

sample({ clock: processingFinished, fn: () => undefined, target: stopPollingFx });

// Отмена должна сразу увести с /processing — иначе polling успеет
// получить cancelled link и UI снова застрянет в лоадере.
sample({ clock: cancelSearchFx.done, fn: (): View => 'onboard', target: navigated });

// 401 на любом запросе или явный logout — немедленно остановить polling.
// Иначе pollTimer продолжает дёргать /current с dead-token каждые 5 секунд
// (бесконечный цикл httpUnauthorized → loggedOut → httpUnauthorized).
sample({ clock: [httpUnauthorized, loggedOut], fn: () => undefined, target: stopPollingFx });

// $selectedReportId сбрасываем в четырёх случаях:
// 1) старт нового search'а — пользователь начал заново, открытый ранее
//    отчёт из ReportHistory больше не «активный». Без этого race:
//    /processing → completed → /report, но queryKey [..., selectedReportId]
//    всё ещё держит UUID старого, и Report.tsx показывает кэш до момента
//    completed-обработчика.
// 2) завершение search'а как accepted (страховка на случай если 1 не
//    сработал по любой причине — повторный сброс не вреден).
// 3) logout — чтобы при логине другого юзера чужой id из cache не
//    утёк в его новые запросы.
// 4) cancelSearchFx.done — раньше пропускали: cancel вызывал searchReset
//    (сбрасывает $searchId/$progress), но $selectedReportId оставался
//    с UUID отменённой/предыдущей search'и. Следующий заход в Report.tsx
//    видел тот же queryKey [..., selectedReportId] с staleTime: Infinity →
//    react-query возвращал старый report без перезапроса. Симптом:
//    «следующий поиск проходит супер быстро» (на самом деле UI просто
//    показывает старый кэш отчёта).
sample({ clock: searchStarted, fn: () => null, target: reportSelected });
sample({
  clock: processingFinished,
  filter: (status) => status === 'completed',
  fn: () => null,
  target: reportSelected,
});
sample({ clock: loggedOut, fn: () => null, target: reportSelected });
sample({ clock: cancelSearchFx.done, fn: () => null, target: reportSelected });
